/* Inline page editing kit.
 *
 * Knowledge-base detail pages render the same layout for viewers and
 * editors; an editor (admin or owner — decided server-side via the
 * `viewer.canEdit` flag) gets a View/Edit switch that flips the page
 * into edit mode. In edit mode, field primitives turn into inputs that
 * auto-save on blur/change. Fields the current viewer may not change
 * are rendered greyed-out + disabled rather than hidden, so everyone
 * sees the same shape.
 *
 *   <EditModeProvider canEdit={viewer.canEdit}>
 *     <EntityShell headerAction={<EditSwitch/>} …>
 *       <EditableText value={x} onSave={v => patch({field:v})}/>
 *     </EntityShell>
 *   </EditModeProvider>
 *
 * The provider also renders the floating mobile switch (CSS shows it
 * only on narrow screens; the header EditSwitch hides there).
 */
import React from 'react';

const EditModeContext = React.createContext({ canEdit: false, editing: false, setEditing: () => {} });

export const useEditMode = () => React.useContext(EditModeContext);

export const EditModeProvider = ({ canEdit = false, editing: editingProp, onEditingChange, children }) => {
  const [internal, setInternal] = React.useState(false);
  // Controlled when the parent passes editing/onEditingChange (e.g. a
  // page that also needs to read edit state at its own top level);
  // otherwise the provider owns the state.
  const editing = editingProp !== undefined ? editingProp : internal;
  const setEditing = onEditingChange || setInternal;
  // If edit rights disappear (e.g. data reloads for a different entity),
  // drop back to view mode so we never leave a stale editor open.
  React.useEffect(() => { if (!canEdit && editing) setEditing(false); }, [canEdit, editing, setEditing]);
  const value = React.useMemo(() => ({ canEdit, editing, setEditing }), [canEdit, editing, setEditing]);
  return (
    <EditModeContext.Provider value={value}>
      {children}
      {canEdit && <MobileEditSwitch/>}
    </EditModeContext.Provider>
  );
};

/* The View / Edit segmented toggle. Renders nothing when the viewer
 * can't edit. Used in the page header on desktop. */
export const EditSwitch = ({ className = '' }) => {
  const { canEdit, editing, setEditing } = useEditMode();
  if (!canEdit) return null;
  return (
    <div className={`edit-switch ${className}`} role="group" aria-label="View or edit this page">
      <button
        type="button"
        className={`edit-switch__opt ${!editing ? 'edit-switch__opt--on' : ''}`}
        aria-pressed={!editing}
        onClick={() => setEditing(false)}
      >
        View
      </button>
      <button
        type="button"
        className={`edit-switch__opt ${editing ? 'edit-switch__opt--on' : ''}`}
        aria-pressed={editing}
        onClick={() => setEditing(true)}
      >
        Edit
      </button>
    </div>
  );
};

/* Floating bottom switch for mobile — rendered by the provider. CSS
 * keeps it hidden on wide screens. */
const MobileEditSwitch = () => (
  <div className="edit-switch__floating-wrap">
    <EditSwitch className="edit-switch--floating"/>
  </div>
);

/* Small "Saved ✓" / error flash shared by the field primitives. */
const SaveFlash = ({ state, error }) => {
  if (error) return <span className="inline-edit__flash inline-edit__flash--err" title={error}>· save failed</span>;
  if (state === 'saving') return <span className="inline-edit__flash">· saving…</span>;
  if (state === 'saved') return <span className="inline-edit__flash inline-edit__flash--ok">· saved</span>;
  return null;
};

/**
 * Auto-saving text / textarea field.
 *
 *   value         current value (string|null)
 *   onSave        async (next:string|null) => void   — called on blur if changed
 *   canEditField  false → rendered greyed + disabled even in edit mode
 *   multiline     textarea instead of input
 *   viewRender    optional (value) => node for the read-only presentation
 *                 (e.g. a Markdown renderer). Defaults to plain text.
 *   placeholder   shown in edit mode when empty
 */
export const EditableText = ({
  value,
  onSave,
  canEditField = true,
  multiline = false,
  rows = 4,
  maxLength,
  placeholder = 'Add…',
  emptyView = '—',
  viewRender,
  inputClassName,
  inputStyle,
}) => {
  const { canEdit, editing } = useEditMode();
  const [draft, setDraft] = React.useState(value ?? '');
  const [state, setState] = React.useState('idle');   // idle | saving | saved
  const [error, setError] = React.useState(null);

  React.useEffect(() => { setDraft(value ?? ''); }, [value]);

  const active = editing && canEdit;

  if (!active) {
    if (viewRender) return viewRender(value);
    if (value === null || value === undefined || value === '') {
      return <span style={{ color: 'var(--fg-muted)' }}>{emptyView}</span>;
    }
    return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
  }

  const commit = async () => {
    const cur = value ?? '';
    if (draft === cur) return;
    const next = draft.trim() === '' ? null : draft;
    setState('saving'); setError(null);
    try {
      await onSave(next);
      setState('saved');
      setTimeout(() => setState('idle'), 1600);
    } catch (e) {
      setError(e.message || String(e));
      setState('idle');
      setDraft(cur);   // revert optimistic edit
    }
  };

  const disabled = !canEditField || state === 'saving';
  const common = {
    value: draft,
    disabled,
    maxLength,
    placeholder,
    title: canEditField ? undefined : 'You don’t have permission to edit this field',
    onChange: e => setDraft(e.target.value),
    onBlur: commit,
    className: `${multiline ? 'textarea' : 'input'} inline-edit__field ${!canEditField ? 'inline-edit__field--locked' : ''} ${inputClassName || ''}`,
    style: inputStyle,
  };

  return (
    <div className="inline-edit">
      {multiline
        ? <textarea rows={rows} {...common}/>
        : <input type="text" {...common}/>}
      <SaveFlash state={state} error={error}/>
    </div>
  );
};

/**
 * Auto-saving <select>. options: [{value,label}]. Same gating + flash.
 */
export const EditableSelect = ({
  value,
  options = [],
  onSave,
  canEditField = true,
  emptyView = '—',
  viewRender,
  placeholder = '— pick —',
  allowEmpty = false,
}) => {
  const { canEdit, editing } = useEditMode();
  const [state, setState] = React.useState('idle');
  const [error, setError] = React.useState(null);

  const active = editing && canEdit;

  if (!active) {
    const opt = options.find(o => o.value === value);
    if (viewRender) return viewRender(value, opt);
    if (!opt) return <span style={{ color: 'var(--fg-muted)' }}>{emptyView}</span>;
    return <span>{opt.label}</span>;
  }

  const onChange = async e => {
    const next = e.target.value || (allowEmpty ? null : '');
    if (next === (value ?? '')) return;
    setState('saving'); setError(null);
    try {
      await onSave(next === '' ? null : next);
      setState('saved');
      setTimeout(() => setState('idle'), 1600);
    } catch (err) {
      setError(err.message || String(err));
      setState('idle');
    }
  };

  return (
    <div className="inline-edit">
      <select
        className={`select inline-edit__field ${!canEditField ? 'inline-edit__field--locked' : ''}`}
        value={value ?? ''}
        disabled={!canEditField || state === 'saving'}
        title={canEditField ? undefined : 'You don’t have permission to edit this field'}
        onChange={onChange}
      >
        {(allowEmpty || value == null) && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <SaveFlash state={state} error={error}/>
    </div>
  );
};
